import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, Mail, MapPin, Clock, Send,
  CheckCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trainingOptions = [
  { value: 'wash', label: 'WASH Trainings' },
  { value: 'agriculture', label: 'Agriculture Trainings' },
  { value: 'waste', label: 'Waste Management & Recycling' },
  { value: 'food', label: 'Food Science Trainings' },
  { value: 'other', label: 'Other / General Inquiry' },
];

const educationLevels = [
  { value: 'primary', label: 'Primary Education' },
  { value: 'secondary', label: 'Secondary / O-Level' },
  { value: 'alevel', label: 'A-Level' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'degree', label: 'Bachelor\'s Degree or Higher' },
  { value: 'other', label: 'Other' },
];

export default function ContactPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [enrollmentForm, setEnrollmentForm] = useState({
    name: '',
    email: '',
    phone: '',
    training_category: '',
    training_course: '',
    education_level: '',
    message: ''
  });

  const [contactLoading, setContactLoading] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setContactLoading(false);
    }
  };

  const handleEnrollmentSubmit = async (e) => {
    e.preventDefault();
    setEnrollmentLoading(true);

    try {
      await axios.post(`${API}/enrollment`, enrollmentForm);
      setEnrollmentSuccess(true);
      toast.success('Enrollment request submitted! We will contact you with more details.');
      setEnrollmentForm({
        name: '',
        email: '',
        phone: '',
        training_category: '',
        training_course: '',
        education_level: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit enrollment. Please try again.');
      console.error('Enrollment form error:', error);
    } finally {
      setEnrollmentLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-primary" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">Get in Touch</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 font-heading">
              Contact Us
            </h1>
            <p className="text-xl text-blue-100">
              Have questions about our trainings or want to partner with us? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-12 bg-accent" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Limbe HQ</p>
                <p className="text-sm text-slate-600">Fako Division, SWR, Cameroon</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Tiko Centre</p>
                <p className="text-sm text-slate-600">CDC Tiko - Towards SS Club</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Phone / WhatsApp</p>
                <a href="tel:+237676837068" className="text-sm text-slate-600 hover:text-primary">
                  +237 676 837 068
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Email</p>
                <a href="mailto:smartcentrecameroon@gmail.com" className="text-sm text-slate-600 hover:text-primary break-all">
                  smartcentrecameroon@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="forms-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 font-heading">
                Send Us a Message
              </h2>
              
              {contactSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <CheckCircle className="h-16 w-16 text-secondary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Message Sent!</h3>
                  <p className="text-slate-600">Thank you for contacting us. We will get back to you soon.</p>
                  <button
                    onClick={() => setContactSuccess(false)}
                    className="mt-4 text-primary font-medium hover:text-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Full Name *</Label>
                    <Input
                      id="contact-name"
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="Enter your full name"
                      className="form-input"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email Address *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="Enter your email"
                      className="form-input"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone">Phone Number</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="Enter your phone number"
                      className="form-input"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      required
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="How can we help you?"
                      className="form-input resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-4 font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {contactLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Enrollment Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 font-heading">
                Enroll in a Training
              </h2>
              
              {enrollmentSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <CheckCircle className="h-16 w-16 text-secondary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Enrollment Submitted!</h3>
                  <p className="text-slate-600">Thank you for your interest. We will contact you with training details.</p>
                  <button
                    onClick={() => setEnrollmentSuccess(false)}
                    className="mt-4 text-primary font-medium hover:text-secondary"
                  >
                    Submit Another Enrollment
                  </button>
                </div>
              ) : (
                <form onSubmit={handleEnrollmentSubmit} className="space-y-6 bg-accent p-8 rounded-2xl" data-testid="enrollment-form">
                  <div className="space-y-2">
                    <Label htmlFor="enroll-name">Full Name *</Label>
                    <Input
                      id="enroll-name"
                      type="text"
                      required
                      value={enrollmentForm.name}
                      onChange={(e) => setEnrollmentForm({ ...enrollmentForm, name: e.target.value })}
                      placeholder="Enter your full name"
                      className="form-input bg-white"
                      data-testid="enroll-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="enroll-email">Email Address *</Label>
                      <Input
                        id="enroll-email"
                        type="email"
                        required
                        value={enrollmentForm.email}
                        onChange={(e) => setEnrollmentForm({ ...enrollmentForm, email: e.target.value })}
                        placeholder="Enter your email"
                        className="form-input bg-white"
                        data-testid="enroll-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="enroll-phone">Phone Number *</Label>
                      <Input
                        id="enroll-phone"
                        type="tel"
                        required
                        value={enrollmentForm.phone}
                        onChange={(e) => setEnrollmentForm({ ...enrollmentForm, phone: e.target.value })}
                        placeholder="Enter your phone"
                        className="form-input bg-white"
                        data-testid="enroll-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="training-category">Training Category *</Label>
                    <Select
                      value={enrollmentForm.training_category}
                      onValueChange={(value) => setEnrollmentForm({ ...enrollmentForm, training_category: value })}
                    >
                      <SelectTrigger className="bg-white" data-testid="training-category-select">
                        <SelectValue placeholder="Select a training category" />
                      </SelectTrigger>
                      <SelectContent>
                        {trainingOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="training-course">Specific Course Interest</Label>
                    <Input
                      id="training-course"
                      type="text"
                      value={enrollmentForm.training_course}
                      onChange={(e) => setEnrollmentForm({ ...enrollmentForm, training_course: e.target.value })}
                      placeholder="e.g., Mushroom Farming, Hand-Drilled Boreholes"
                      className="form-input bg-white"
                      data-testid="training-course-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="education-level">Education Level</Label>
                    <Select
                      value={enrollmentForm.education_level}
                      onValueChange={(value) => setEnrollmentForm({ ...enrollmentForm, education_level: value })}
                    >
                      <SelectTrigger className="bg-white" data-testid="education-level-select">
                        <SelectValue placeholder="Select your education level" />
                      </SelectTrigger>
                      <SelectContent>
                        {educationLevels.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="enroll-message">Additional Information</Label>
                    <Textarea
                      id="enroll-message"
                      rows={3}
                      value={enrollmentForm.message}
                      onChange={(e) => setEnrollmentForm({ ...enrollmentForm, message: e.target.value })}
                      placeholder="Any questions or specific interests?"
                      className="form-input bg-white resize-none"
                      data-testid="enroll-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={enrollmentLoading}
                    className="w-full bg-secondary text-white hover:bg-secondary-hover rounded-full px-8 py-4 font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="enroll-submit-btn"
                  >
                    {enrollmentLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Submit Enrollment
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-accent" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-heading">
              Find Us
            </h2>
            <p className="text-slate-600 mt-2">Visit our headquarters in Limbe, Cameroon</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127357.01246887396!2d9.124833!3d4.0234926!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1061137e5f0e0e1b%3A0x4c7f4d0eb4c56e4a!2sLimbe%2C%20Cameroon!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SMART Centre Cameroon Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
